import { OnboardingSchema } from '@features/onboarding/schema/onboarding';
import { Application, Applicant, Medication, IssueDetail, Condition } from 'src/services/interfaces/application';
import { InsurancePlan } from 'src/services/interfaces/insurance';
import { MONTH_MAP } from '@constants/index';

const sanitizeCategory = (category: string) =>
  category.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

// Helper to get month name from number (e.g., 02 -> February)
const getMonthName = (monthNumber: string) => {
  const monthIndex = parseInt(monthNumber, 10) - 1;
  return Object.keys(MONTH_MAP).find(
    (key) => MONTH_MAP[key as keyof typeof MONTH_MAP] === (monthIndex + 1).toString().padStart(2, '0')
  ) || '';
};

export function transformApplicationToOnboardingData(
  application: Application,
  allPlans: InsurancePlan[]
): Partial<OnboardingSchema> {
  if (!application) return {};

  const transformedData: Partial<OnboardingSchema> = {};

  // 1. Transform Applicants
  if (application.applicants && application.applicants.length > 0) {
    // Set householdIncome at root level for primary applicant
    const primaryApplicant = application.applicants.find(app => app.applicant === 1);
    if (primaryApplicant) {
      transformedData.householdIncome = application.income;
    }

    transformedData.applicants = application.applicants.map((app: Applicant) => {
      const [year, month, day] = app.dob.split('-');
      return {
        applicant: app.applicant === 1 ? 'primary' : app.applicant === 2 ? 'spouse' : 'dependent',
        firstName: app.firstName,
        lastName: app.lastName,
        midName: app.midName || '',
        gender: app.gender || 'male', // Default to 'male' to satisfy schema
        dateOfBirth: {
          year,
          month: getMonthName(month),
          day,
        },
        height: {
          ft: app.heightFt ?? 0, // Default to 0
          in: app.heightIn ?? 0,  // Default to 0
        },
        weight: app.weight ?? 0, // Default to 0
        nicotine: app.nicotine,
      };
    });
  }

  // 2. Transform Plans
  if (application.plans && allPlans.length > 0) {
    application.plans.forEach((plan) => {
      const fullPlan = allPlans.find((p: InsurancePlan) => p.id === plan.id);
      if (fullPlan && fullPlan.category) {
        const planFieldName =
          fullPlan.category === 'Major Medical'
            ? 'medicalPlan'
            : `${sanitizeCategory(fullPlan.category)}Plan`;
        (transformedData as unknown as Record<string, unknown>)[planFieldName] = fullPlan.uid;

        // Set tier for the plan
        if (plan.tier) {
          const tierFieldName = planFieldName.replace('Plan', 'PlanTier');
          (transformedData as unknown as Record<string, unknown>)[tierFieldName] = plan.tier;
        }

        // Set coverage amount for the plan (used in Critical Illness)
        if (plan.coverageAmount) {
          const coverageAmountFieldName = planFieldName.replace('Plan', 'PlanCoverageAmount');
          (transformedData as unknown as Record<string, unknown>)[coverageAmountFieldName] = plan.coverageAmount;
        }

        // Set coverage tags for the plan
        if (plan.applicants && plan.applicants.length > 0) {
            const tags: string[] = [];
          if (fullPlan.category === 'Major Medical') {
            // For medical plans, use medicalTags
            if (plan.applicants.includes(1)) {
              tags.push('spouse_1');
            }
            plan.applicants.forEach((appIndex) => {
              if (appIndex >= 2) {
                tags.push(`dependent_${appIndex}`);
              }
            });
            transformedData.medicalTags = tags;
          } else {
            // For other plans, use planTags
            if (!transformedData.planTags) {
              transformedData.planTags = {};
            }
            const planTags = transformedData.planTags as Record<string, string[]>;
            planTags[fullPlan.uid] = [];
            if (plan.applicants.includes(1)) {
              planTags[fullPlan.uid].push('spouse_1');
            }
            plan.applicants.forEach((appIndex) => {
              if (appIndex >= 2) {
                planTags[fullPlan.uid].push(`dependent_${appIndex}`);
              }
            });
          }
        }
      }
    });
  }

  // 3. Transform Address and SSN
  if (application.address && typeof application.address === 'object') {
    transformedData.address = {
      address1: application.address.address1,
      address2: application.address.address2,
      city: application.address.city,
      state: application.address.state,
      zipcode: application.address.zipcode,
      country: application.address.country,
    };
  }

  if (application.ssn) {
    transformedData.ssn = application.ssn;
  }

  // 4. Transform PHQ
  if (application.phq) {
    // Handle both old format (IssueDetail[]) and new format (with details)
    const issuesData = application.phq.issues;
    const surgeriesConditions: { value: string; details?: string }[] = [];
    const medDetailsConditions: { value: string; details?: string }[] = [];

    // Process issues - handle both formats
    if (Array.isArray(issuesData)) {
      issuesData.forEach((issue: IssueDetail | { key: string; details?: Array<{ key: string; description?: string }> }) => {
        // Check if this is the new format with details
        if ('details' in issue && issue.details && Array.isArray(issue.details)) {
          // New format: { key: string, details: Array }
          surgeriesConditions.push({ value: issue.key });

          // Add details to medDetails
          issue.details.forEach((detail: { key: string; description?: string }) => {
            if (detail.key && detail.description) {
              const detailValue = `${issue.key}-${detail.key}`;
              medDetailsConditions.push({
                value: detailValue,
                details: detail.description
              });
            }
          });
        } else {
          surgeriesConditions.push({ value: (issue as IssueDetail).key });
        }
      });
    }

    transformedData.healthQuestions = {
      initialQuestions: {
        treatment: application.phq.treatment,
        invalid: application.phq.invalid,
        pregnancy: application.phq.pregnancy,
      },
      takingMedicationsNow: {
        value: application.phq.medications && application.phq.medications.length > 0,
        medications: application.phq.medications && Array.isArray(application.phq.medications) 
          ? application.phq.medications.map(med => {
              // Convert applicant number to string format for dropdown
              let applicantString = '0'; // default to primary
              if (typeof med.applicant === 'number') {
                applicantString = med.applicant === 0 ? '0' : med.applicant === 1 ? '1' : '2';
              } else if (typeof med.applicant === 'string') {
                // If it's already a string, ensure it's in the correct format
                applicantString = med.applicant === 'primary' || med.applicant === '0' ? '0' : 
                                 med.applicant === 'spouse' || med.applicant === '1' ? '1' : '2';
              }

              return {
                name: med.name,
                rxcui: med.rxcui,
                form: med.form,
                dosage: med.dosage,
                frequency: med.frequency,
                frequencyDescription: med?.frequencyDescription || '',
                applicant: applicantString,
                description: med.description
              };
            })
          : []
      },
      surgeriesDiabetesHeartMental: {
        conditions: surgeriesConditions,
      },
      medDetails: {
        medications: [
          ...medDetailsConditions,
          ...(application.phq.medications ? application.phq.medications.map((med: Medication) => ({ 
            value: med.name,
            details: med.description || ''
          })) : [])
        ],
        details: {
          ...(application.phq.medications ? application.phq.medications.reduce((acc: Record<string, string>, med: Medication) => {
            if (med.description) {
              acc[med.name] = med.description;
            }
            return acc;
          }, {} as Record<string, string>) : {}),
          ...medDetailsConditions.reduce((acc: Record<string, string>, condition) => {
            if (condition.details) {
              acc[condition.value] = condition.details;
            }
            return acc;
          }, {} as Record<string, string>)
        }
      },
      otherHealthConditions: {
        conditions: application.phq.conditions ? application.phq.conditions.map((condition: Condition) => ({ value: condition.key })) : [],
        details: application.phq.conditions ? application.phq.conditions.reduce((acc: Record<string, string>, condition: Condition) => {
          if (condition.description) {
            acc[condition.key] = condition.description;
          }
          return acc;
        }, {} as Record<string, string>) : {}
      },
      coverageStartDate: {
        effectiveDate: application.phq.effectiveDate,
      },
      privacyDisclosure: {
        disclaimer: application.phq.disclaimer,
        signature: application.phq.signature,
        accepted: !!application.phq.signature,
      },
      // NOTE: other fields would need mapping if required
    } as OnboardingSchema['healthQuestions'];
  }

  return transformedData;
} 